import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "../Button";
import {
	StyledDropzone,
	StyledFileList,
	StyledFileItem,
	StyledErrorMessage,
	StyledProgressBar,
} from "../../styles/StyledDocumentUpload";
import UploadIcon from "../../assets/img/icons/upload.svg";
import PdfIcon from "../../assets/img/icons/pdf.svg";
import ImageIcon from "../../assets/img/icons/image.svg";
import JpegIcon from "../../assets/img/icons/jpeg.svg";
import PngIcon from "../../assets/img/icons/png.svg";
import DeleteIcon from "../../assets/img/icons/delete.svg";

export default function DocumentDropzone({ 
	onFilesSelected, 
	selectedFiles, 
	onFileRemove, 
	errors, 
	uploadProgress 
}) {
	const [isDragActive, setIsDragActive] = useState(false);

	const handleDrop = useCallback((e) => {
		e.preventDefault();
		setIsDragActive(false);
		
		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			onFilesSelected(files);
		}
	}, [onFilesSelected]);

	const handleDragOver = useCallback((e) => {
		e.preventDefault();
	}, []);

	const handleDragEnter = useCallback((e) => {
		e.preventDefault();
		setIsDragActive(true);
	}, []);

	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		// Only set drag inactive if we're leaving the dropzone completely
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsDragActive(false);
		}
	}, []);

	const handleFileInputChange = useCallback((e) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			onFilesSelected(files);
		}
		// Reset input value to allow selecting the same file again
		e.target.value = "";
	}, [onFilesSelected]);

	const handleDropzoneClick = useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = false; // Single file only
		input.accept = ".pdf,.jpeg,.png";
		input.onchange = handleFileInputChange;
		input.click();
	}, [handleFileInputChange]);

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const getFileIcon = (fileType) => {
		switch (fileType) {
			case "application/pdf":
				return PdfIcon;
			case "image/jpeg":
			case "image/jpg":
				return JpegIcon;
			case "image/png":
				return PngIcon;
			default:
				if (fileType.startsWith("image/")) {
					return ImageIcon;
				}
				return PdfIcon; // Default to PDF icon
		}
	};

	return (
		<div style={{ 
			width: "100%",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center"
		}}>
			<StyledDropzone
				isDragActive={isDragActive}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onClick={handleDropzoneClick}
				style={{
					minHeight: "300px",
					width: "100%",
					maxWidth: "500px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center"
				}}
			>
				<div className="dropzone-content">
					<img 
						src={UploadIcon} 
						alt="Upload" 
						className="dropzone-icon"
					/>
					<p className="dropzone-text">
						{isDragActive 
							? "Drop document here..." 
							: "Drag and drop a document here, or click to browse"
						}
					</p>
					<p className="dropzone-subtext">
						Supports PDF, PNG, JPEG files up to 5MB
					</p>
				</div>
			</StyledDropzone>

			{errors.length > 0 && (
				<div style={{ marginTop: "1.5rem", width: "100%", maxWidth: "500px" }}>
					{errors.map((error, index) => (
						<StyledErrorMessage key={index}>
							<span className="error-icon">⚠️</span>
							{error}
						</StyledErrorMessage>
					))}
				</div>
			)}
		</div>
	);
}

DocumentDropzone.propTypes = {
	onFilesSelected: PropTypes.func.isRequired,
	selectedFiles: PropTypes.array.isRequired,
	onFileRemove: PropTypes.func.isRequired,
	errors: PropTypes.array.isRequired,
	uploadProgress: PropTypes.object.isRequired,
};